import { Controller } from "@hotwired/stimulus"

export default class ProductCarouselController extends Controller {
  static targets = ["track", "prev", "next", "item", "dot"]
  static values = {
    activeIndex: { type: Number, default: 0 },
  }

  declare readonly trackTarget: HTMLElement
  declare readonly prevTarget: HTMLButtonElement
  declare readonly nextTarget: HTMLButtonElement
  declare readonly itemTargets: HTMLElement[]
  declare readonly dotTargets: HTMLElement[]
  declare activeIndexValue: number

  private ticking = false
  private handleScroll = () => this.requestSyncActive()

  connect() {
    this.syncActiveToClosestCenter()
    this.trackTarget.addEventListener("scroll", this.handleScroll, { passive: true })
  }

  disconnect() {
    this.trackTarget.removeEventListener("scroll", this.handleScroll)
  }

  next() {
    this.scrollToIndex(this.activeIndexValue + 1)
  }

  prev() {
    this.scrollToIndex(this.activeIndexValue - 1)
  }

  goTo({ params }: { params: { index: number } }) {
    this.scrollToIndex(params.index)
  }

  private scrollToIndex(index: number) {
    const clamped = Math.max(0, Math.min(index, this.itemTargets.length - 1))
    this.itemTargets[clamped]?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" })
  }

  private requestSyncActive() {
    if (this.ticking) return
    this.ticking = true
    requestAnimationFrame(() => {
      this.syncActiveToClosestCenter()
      this.ticking = false
    })
  }

  // Finds whichever item's midpoint sits closest to the track's visible center
  // and marks it active. Scroll-position math rather than IntersectionObserver
  // thresholds, since item width (and therefore what fraction of an item counts
  // as "centered") varies a lot across breakpoints (70% of track on mobile down
  // to 20% on desktop).
  private syncActiveToClosestCenter() {
    if (this.itemTargets.length === 0) return
    const trackCenter = this.trackTarget.scrollLeft + this.trackTarget.clientWidth / 2

    let closestIndex = 0
    let closestDistance = Infinity
    this.itemTargets.forEach((item, i) => {
      const itemCenter = item.offsetLeft + item.offsetWidth / 2
      const distance = Math.abs(itemCenter - trackCenter)
      if (distance < closestDistance) {
        closestDistance = distance
        closestIndex = i
      }
    })

    this.setActive(closestIndex)
  }

  private setActive(index: number) {
    if (index === this.activeIndexValue && this.itemTargets[index]?.dataset.active === "true") return
    this.activeIndexValue = index
    this.itemTargets.forEach((item, i) => {
      item.dataset.active = String(i === index)
    })
    this.dotTargets.forEach((dot, i) => {
      dot.classList.toggle("carousel-dot--active", i === index)
    })
    this.updateButtons()
  }

  private updateButtons() {
    this.prevTarget.disabled = this.activeIndexValue <= 0
    this.nextTarget.disabled = this.activeIndexValue >= this.itemTargets.length - 1
  }
}
